// components/doctrine/MemoryPane.tsx
// The user's memory, one list for every agent (the Daimon's included): add, read, erase. Shown in
// the Doctrine view and in Settings > Account.
import React, { useCallback, useEffect, useState } from 'react';
import { Trash2 } from '@/components/ui/lucide-memo';
import type { DoctrineMemoryEntryDTO } from '#types/types';

const MemoryRowImpl = ({
  entry,
  onRemove,
}: {
  entry: DoctrineMemoryEntryDTO;
  onRemove: (id: string) => void;
}) => {
  const handleRemove = useCallback(() => { onRemove(entry.id); }, [onRemove, entry.id]);
  return (
    <li className="group -mx-3 flex items-start gap-3 rounded-[8px] px-3 py-2.5 transition-colors hover:bg-white/[0.045]">
      <span
        className={
          'mt-[2px] shrink-0 rounded-[5px] px-1.5 py-0.5 text-[11px] font-medium ' +
          (entry.origin === 'AGENT_VOLUNTEERED' ? 'bg-[#1E2A3C] text-[#7FA6DC]' : 'bg-[#242424] text-[#8A8A8A]')
        }
        title={entry.origin === 'AGENT_VOLUNTEERED' ? 'Recorded by an agent' : 'Recorded by you'}
      >
        {entry.origin === 'AGENT_VOLUNTEERED' ? 'Agent' : 'You'}
      </span>
      <span className="min-w-0 flex-1 text-[15px] leading-relaxed text-[#C8C8C8]">{entry.content}</span>
      <button
        type="button"
        onClick={handleRemove}
        className="mt-0.5 shrink-0 text-[#4A4A4A] opacity-0 transition-opacity hover:text-[#E06C6C] group-hover:opacity-100"
        aria-label="Delete memory"
      >
        <Trash2 size={14} />
      </button>
    </li>
  );
};
const MemoryRow = React.memo(MemoryRowImpl);
MemoryRow.displayName = 'MemoryRow';

export const MemoryPane = ({ initial }: { initial: DoctrineMemoryEntryDTO[] }) => {
  const [entries, setEntries] = useState(initial);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- pre-existing pattern in this file; re-seeding the editable list when the server copy changes IS this effect's purpose
    setEntries(initial);
  }, [initial]);

  const add = useCallback(async () => {
    const content = draft.trim();
    if (!content) return;
    setBusy(true);
    setError(null);
    try {
      const res = await fetch('/api/doctrine', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content }),
      });
      if (!res.ok) throw new Error(`Add failed (${res.status})`);
      const data: { entry: DoctrineMemoryEntryDTO } = await res.json();
      setEntries((prev) => [data.entry, ...prev.filter((e) => e.id !== data.entry.id)]);
      setDraft('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Add failed');
    } finally {
      setBusy(false);
    }
  }, [draft]);

  const remove = useCallback(async (id: string) => {
    setEntries((prev) => prev.filter((e) => e.id !== id));
    await fetch(`/api/doctrine?id=${encodeURIComponent(id)}`, { method: 'DELETE' }).catch(() => {});
  }, []);

  const handleDraftChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => setDraft(e.target.value), []);

  const handleDraftKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        void add();
      }
    },
    [add],
  );

  const handleAddClick = useCallback(() => { void add(); }, [add]);

  return (
    <div className="flex flex-col gap-1 pt-1">
      <div className="mb-2 flex items-center gap-2 border-b border-[#242424] pb-3">
        <input
          value={draft}
          onChange={handleDraftChange}
          onKeyDown={handleDraftKeyDown}
          placeholder="Add something the agents should remember about you…"
          className="flex-1 border-0 bg-transparent p-0 text-[15px] text-[#D4D4D4] outline-none placeholder:text-[#4A4A4A]"
        />
        <button
          type="button"
          onClick={handleAddClick}
          disabled={busy || !draft.trim()}
          className={
            'rounded-[6px] px-3 py-1 text-[13px] transition-colors ' +
            (busy || !draft.trim() ? 'text-[#4A4A4A]' : 'text-[#8FB4E8] hover:bg-[#243247]')
          }
        >
          Add
        </button>
      </div>
      {error && <div className="pb-2 text-[12px] text-[#E06C6C]">{error}</div>}

      {entries.length === 0 ? (
        <div className="pt-3 text-[14px] text-[#6E6E6E]">
          No memory yet. Add one above, or tell an agent to remember something during a chat.
        </div>
      ) : (
        <ul className="flex flex-col gap-0.5">
          {entries.map((entry) => (
            <MemoryRow key={entry.id} entry={entry} onRemove={remove} />
          ))}
        </ul>
      )}
    </div>
  );
};

/** Settings' Memory section: what every agent remembers about you, as ChatGPT's "Manage memory". */
export const MemorySettingsSection = () => {
  const [entries, setEntries] = useState<DoctrineMemoryEntryDTO[] | null>(null);
  useEffect(() => {
    let cancelled = false;
    fetch('/api/doctrine?key=user%3Amemory')
      .then((res) => (res.ok ? res.json() : null))
      .then((body: { memory?: DoctrineMemoryEntryDTO[] } | null) => { if (!cancelled) setEntries(body?.memory ?? []); })
      .catch(() => { if (!cancelled) setEntries([]); });
    return () => { cancelled = true; };
  }, []);
  return (
    <section className="mt-8" aria-labelledby="account-memory-heading">
      <h2 id="account-memory-heading" className="text-[17px] font-medium leading-6 text-[#ECECEC]">Memory</h2>
      <p className="mt-1 pb-3 text-[14px] leading-5 text-[#8E8E8E]">What the Daimon and Fater&apos;s agents remember about you. Erase anything, or add what they should know.</p>
      {entries ? <MemoryPane initial={entries} /> : <p className="text-[14px] text-[#8E8E8E]">Loading…</p>}
    </section>
  );
};
